import { createAsyncThunk } from "@reduxjs/toolkit";

import { createProjectAddon as createProjectAddonRequest } from "Api/organizations/projects/createProjectAddon.request";
import { deleteProjectAddon as deleteProjectAddonRequest } from "Api/organizations/projects/deleteProjectAddon.request";
import { updateProjectAddon } from "Api/organizations/projects/updateProjectAddon.request";
import { validateAPIError } from "Api/utils";
import { AsyncThunkOptionType } from "Reducers/types";

import { projectAddonsByTypeSelector } from "../projectAddons.selectors";

import type {
  ProjectAddonUpdateResult,
  UpdateProjectAddonsRejection
} from "../types";

export const updateProjectAddons = createAsyncThunk<
  ProjectAddonUpdateResult[],
  {
    organizationId: string;
    projectId: string;
    updates: Record<string, number | string>;
  },
  AsyncThunkOptionType<UpdateProjectAddonsRejection>
>(
  "app/organization/projects/updateAddons",
  async (
    { organizationId, projectId, updates },
    { rejectWithValue, getState }
  ) => {
    const addonsByType = projectAddonsByTypeSelector(getState(), {
      projectId
    });

    const promises = Object.entries(updates).map(
      async ([type, value]): Promise<ProjectAddonUpdateResult | null> => {
        const existingAddon = addonsByType[type];

        if (typeof value === "string" && value === "") {
          if (!existingAddon?.id) return null;

          await deleteProjectAddonRequest({
            organizationId,
            projectId,
            addonId: existingAddon.id
          });
          return { kind: "remove", addonId: existingAddon.id };
        }

        if (existingAddon?.id) {
          const data =
            typeof value === "number" ? { quantity: value } : { sku: value };

          const addon = await updateProjectAddon({
            organizationId,
            projectId,
            addonId: existingAddon.id,
            data
          });
          return { kind: "upsert", addon };
        }

        if (typeof value === "string") {
          const addon = await createProjectAddonRequest({
            organizationId,
            projectId,
            data: { type, sku: value }
          });
          return { kind: "upsert", addon };
        }

        throw new Error(`Addon type "${type}" does not exist on this project`);
      }
    );

    const results = await Promise.allSettled(promises);
    const fulfilled = results
      .filter(
        (r): r is PromiseFulfilledResult<ProjectAddonUpdateResult | null> =>
          r.status === "fulfilled"
      )
      .map(r => r.value)
      .filter((r): r is ProjectAddonUpdateResult => r !== null);
    const rejected = results.filter(r => r.status === "rejected");

    if (rejected.length > 0) {
      const error = validateAPIError(rejected[0].reason);
      if (fulfilled.length > 0) {
        return rejectWithValue({ error, partialResults: fulfilled });
      }
      return rejectWithValue({ error });
    }

    return fulfilled;
  }
);
